import type {
  AiOperationsCurrentProposal,
  AiOperationsProposalRound,
  AiOperationsRun,
  AiOperationsRunAction,
  ApprovalProposalDetail,
} from "@adpilot/shared";
import { IconCheck, IconChevronRight } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ReviewProposalDialog } from "@/components/approval/review-proposal-dialog";
import { Badge } from "@/components/ui/badge";
import { useSubmitApprovalProposal } from "@/hooks/use-approvals";
import { runDisplayStatus } from "@/lib/ai-run-presentation";
import { getApiErrorMessage } from "@/lib/api-error";
import { useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";
import { type ActionDecisionDraft, buildActionDecisionSubmission } from "./ai-run-decision";
import {
  ActionDecisionSheet,
  HighRiskConfirmationDialog,
} from "./run-decision-record/ActionDecisionSheet";
import { DecisionRecordHeader } from "./run-decision-record/DecisionRecordHeader";
import {
  decisionRecordStatus,
  formatRunDate,
} from "./run-decision-record/decision-record-presentation";
import { FinalizedActionRecord } from "./run-decision-record/FinalizedActionRecord";
import { FullAnalysisDialog } from "./run-decision-record/FullAnalysisDialog";
import { ActionReviewTable, AiSummary } from "./run-decision-record/PendingActionReview";
import { RunDetailsDrawer } from "./run-decision-record/RunDetailsDrawer";
import { FailedRunResult, NoChangesResult } from "./run-decision-record/RunOutcomeResults";

// Status helpers live with the other presentation logic; re-exported so existing importers keep
// this module as their entry point.
export {
  canViewActionImpact,
  decisionLabel,
  decisionRecordStatus,
  finalizedReviewStatus,
} from "./run-decision-record/decision-record-presentation";

interface AiRunDecisionRecordProps {
  actions: AiOperationsRunAction[];
  canDecide: boolean;
  onApplied: () => void;
  proposal: AiOperationsCurrentProposal;
  review?: ApprovalProposalDetail;
  rounds: AiOperationsProposalRound[];
  run: AiOperationsRun;
}

export function AiRunDecisionRecord({
  actions,
  canDecide,
  onApplied,
  proposal,
  review,
  rounds,
  run,
}: AiRunDecisionRecordProps): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [batchReviewOpen, setBatchReviewOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);
  const [confirmDraft, setConfirmDraft] = useState<{
    actionId: string;
    draft: ActionDecisionDraft;
  } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submitMutation = useSubmitApprovalProposal();
  const artifact = proposal.artifact;
  const approvalActions = review?.actions ?? [];
  const pendingActions = approvalActions.filter(({ reviewStatus }) => reviewStatus === "pending");
  const approvalActionCount = artifact.actions.filter(
    ({ server }) => server.route === "approval",
  ).length;
  const decidedCount = approvalActions.filter(
    ({ reviewStatus }) => reviewStatus !== "pending" && reviewStatus !== "not_required",
  ).length;
  const decisionTotal =
    approvalActions.filter(({ route }) => route === "approval").length || approvalActionCount;
  const selectedAction = artifact.actions.find(({ id }) => id === selectedActionId);
  const selectedReviewAction = approvalActions.find(({ id }) => id === selectedActionId);
  const isPendingReview =
    review?.reviewStatus === "pending" && run.status === "suspended" && pendingActions.length > 0;
  const isFinalizing = review?.finalizationStatus === "processing";
  const showNoChanges = artifact.actions.length === 0;
  const displayStatus = runDisplayStatus(run, pendingActions.length > 0, {
    actions,
    proposalActionCount: artifact.actions.length,
    review,
  });
  const showFailure = displayStatus.incident !== null;

  async function submitDecision(actionId: string, draft: ActionDecisionDraft): Promise<void> {
    if (!review) {
      setSubmitError(t("runDetail.decisionRecord.review.validation"));
      return;
    }
    const parsed = buildActionDecisionSubmission(actionId, draft);
    if (!parsed.success) {
      setSubmitError(t("runDetail.decisionRecord.review.validation"));
      return;
    }
    try {
      setSubmitError(null);
      await submitMutation.mutateAsync({
        proposalId: review.id,
        body: parsed.data,
        shopId: run.shopId,
      });
      setConfirmDraft(null);
      setSelectedActionId(null);
      onApplied();
    } catch (error) {
      setConfirmDraft(null);
      setSubmitError(await getApiErrorMessage(error));
    }
  }

  // 页面主体带自述状态区块(收尾横幅/无变更/失败区块)时,头部不再挂状态
  // 徽章——否则"审批完成"在头部和横幅说两遍。徽章只服务待审批视图。
  const showPendingTable =
    !showNoChanges && !(review && (isFinalizing || review.reviewStatus !== "pending"));
  const status = showPendingTable
    ? decisionRecordStatus(
        run,
        review,
        artifact.actions.length,
        pendingActions.length || approvalActionCount,
        t,
      )
    : undefined;

  return (
    <div className="flex min-h-full flex-col gap-[18px] px-6 pb-7 pt-6">
      <DecisionRecordHeader run={run} status={status} onOpenDetails={() => setDetailsOpen(true)} />

      {showNoChanges && showFailure ? (
        <FailedRunResult
          proposal={proposal}
          run={run}
          onOpenAnalysis={() => setAnalysisOpen(true)}
        />
      ) : showNoChanges ? (
        <NoChangesResult proposal={proposal} onOpenAnalysis={() => setAnalysisOpen(true)} />
      ) : review && (isFinalizing || review.reviewStatus !== "pending") ? (
        <FinalizedActionRecord
          actions={actions}
          canRollback={canDecide}
          proposal={proposal}
          review={review}
          run={run}
          onApplied={onApplied}
          onOpenAnalysis={() => setAnalysisOpen(true)}
        />
      ) : (
        <>
          <AiSummary proposal={proposal} onOpenAnalysis={() => setAnalysisOpen(true)} />
          <ActionReviewTable
            canDecide={canDecide && isPendingReview}
            decidedCount={decidedCount}
            decisionTotal={decisionTotal}
            pendingCount={pendingActions.length}
            proposal={proposal}
            review={review}
            onReviewAll={() => setBatchReviewOpen(true)}
            onOpenAction={(actionId) => {
              setSubmitError(null);
              setSelectedActionId(actionId);
            }}
          />
        </>
      )}

      <FullAnalysisDialog open={analysisOpen} onOpenChange={setAnalysisOpen} proposal={proposal} />
      <RunDetailsDrawer
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        proposal={proposal}
        review={review}
        rounds={rounds}
        run={run}
      />
      {review && pendingActions.length > 0 ? (
        <ReviewProposalDialog
          key={`${review.id}:${review.updatedAt}`}
          proposalId={review.id}
          shopId={run.shopId}
          actions={review.actions}
          open={batchReviewOpen}
          onOpenChange={setBatchReviewOpen}
          onApplied={onApplied}
        />
      ) : null}
      {selectedAction ? (
        <ActionDecisionSheet
          key={selectedAction.id}
          action={selectedAction}
          canDecide={canDecide && selectedReviewAction?.reviewStatus === "pending"}
          open
          review={review}
          reviewAction={selectedReviewAction}
          submitting={submitMutation.isPending}
          submitError={submitError}
          onOpenChange={(open) => {
            if (!open) {
              // 提交进行中不允许关闭，否则失败信息随弹层一起丢失。
              if (submitMutation.isPending) return;
              setSubmitError(null);
              setSelectedActionId(null);
            }
          }}
          onSubmit={(draft) => {
            const highRisk =
              selectedAction.server.riskLevel === "high" ||
              selectedAction.server.riskLevel === "critical";
            if (highRisk && draft.decision !== "reject") {
              setConfirmDraft({ actionId: selectedAction.id, draft });
              return;
            }
            void submitDecision(selectedAction.id, draft);
          }}
        />
      ) : null}
      <HighRiskConfirmationDialog
        open={confirmDraft !== null}
        pending={submitMutation.isPending}
        onOpenChange={(open) => {
          if (!open && !submitMutation.isPending) setConfirmDraft(null);
        }}
        onConfirm={() => {
          if (confirmDraft) void submitDecision(confirmDraft.actionId, confirmDraft.draft);
        }}
      />
    </div>
  );
}

interface RevisionProgressProps {
  canOpenDetails?: boolean;
  onApplied?: () => void;
  review: ApprovalProposalDetail;
  rounds: AiOperationsProposalRound[];
  run: AiOperationsRun;
}

export function AiRunRevisionProgress({
  canOpenDetails = true,
  review,
  rounds,
  run,
}: RevisionProgressProps): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { locale } = useFormatters();
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const revisionEvent = [...review.decisionHistory]
    .reverse()
    .find(({ decision, phase }) => decision === "chat" && phase === "applied");
  const closedRound = Math.max(...rounds.map(({ roundNumber }) => roundNumber), 1);
  const nextRound = closedRound + 1;

  return (
    <div className="flex min-h-full flex-col gap-[18px] px-6 pb-7 pt-6">
      <DecisionRecordHeader
        run={run}
        status={{
          tone: "warning",
          label: t("runDetail.decisionRecord.status.generating", { round: nextRound }),
        }}
        subtitle={t("runDetail.decisionRecord.revision.requestedAt", {
          date: formatRunDate(revisionEvent?.createdAt ?? run.startedAt, locale),
        })}
      />

      <section className="flex min-h-[103px] items-start justify-between rounded-[10px] border border-line bg-surface px-[18px] py-4">
        <div className="flex max-w-[820px] flex-col gap-1.5">
          <p className="text-xs font-medium leading-4 text-muted-foreground">
            {t("runDetail.decisionRecord.revision.requestLabel")}
          </p>
          <p className="text-base font-semibold leading-[23px] text-ink">
            {revisionEvent?.note ?? t("runDetail.decisionRecord.revision.requestUnavailable")}
          </p>
          <p className="text-xs leading-[18px] text-muted-foreground">
            {t("runDetail.decisionRecord.revision.submittedBy")}
          </p>
        </div>
        <Badge variant="neutral" className="h-7 px-2.5 text-xs">
          {t("runDetail.decisionRecord.revision.saved")}
        </Badge>
      </section>

      <section className="overflow-hidden rounded-[10px] border border-line bg-surface">
        <div className="border-b border-line px-[18px] py-4">
          <h2 className="text-base font-semibold leading-5 text-ink">
            {t("runDetail.decisionRecord.revision.preparing")}
          </h2>
          <p className="mt-0.5 text-xs leading-4 text-muted-foreground">
            {t("runDetail.decisionRecord.revision.preparingDescription")}
          </p>
        </div>
        <RevisionStep
          icon={<IconCheck />}
          title={t("runDetail.decisionRecord.revision.accepted")}
          description={t("runDetail.decisionRecord.revision.acceptedDescription", {
            round: closedRound,
          })}
          outcome={t("runDetail.decisionRecord.revision.complete")}
          tone="success"
        />
        <RevisionStep
          icon={String(nextRound - closedRound + 1)}
          title={t("runDetail.decisionRecord.revision.analyzing", { round: nextRound })}
          description={t("runDetail.decisionRecord.revision.analyzingDescription")}
          outcome={t("runDetail.decisionRecord.revision.inProgress")}
          tone="warning"
        />
        <RevisionStep
          icon="3"
          title={t("runDetail.decisionRecord.revision.available")}
          description={t("runDetail.decisionRecord.revision.availableDescription")}
          outcome={t("runDetail.decisionRecord.revision.waiting")}
          tone="muted"
          last
        />
      </section>

      <button
        type="button"
        className="flex min-h-[66px] items-center justify-between rounded-[10px] border border-line bg-surface px-4 py-3.5 text-left transition-colors hover:bg-subtle/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => setAnalysisOpen(true)}
      >
        <span className="flex items-center gap-3">
          <span className="flex size-8 items-center justify-center rounded-[8px] bg-subtle text-xs font-semibold text-muted-foreground">
            R{closedRound}
          </span>
          <span>
            <span className="block text-[13px] font-semibold leading-[18px] text-ink">
              {t("runDetail.decisionRecord.revision.previous", {
                actions: review.artifact.actions.length,
              })}
            </span>
            <span className="block text-xs leading-4 text-muted-foreground">
              {t("runDetail.decisionRecord.revision.previousDescription")}
            </span>
          </span>
        </span>
        <span className="text-xs font-medium text-ink">
          {t("runDetail.decisionRecord.revision.viewClosed")} ›
        </span>
      </button>

      {canOpenDetails ? (
        <button
          type="button"
          className="flex items-center justify-between border-t border-line px-0.5 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => setDetailsOpen(true)}
        >
          <span>
            <span className="block text-[13px] font-semibold leading-[18px] text-ink">
              {t("runDetail.decisionRecord.revision.runDetails")}
            </span>
            <span className="block text-xs leading-4 text-muted-foreground">
              {t("runDetail.decisionRecord.revision.runDetailsDescription")}
            </span>
          </span>
          <IconChevronRight className="size-4 text-muted-foreground" />
        </button>
      ) : null}

      <FullAnalysisDialog
        open={analysisOpen}
        onOpenChange={setAnalysisOpen}
        proposal={{
          id: review.id,
          roundNumber: review.strategyContext.proposalRound,
          actionCount: review.artifact.actions.length,
          conclusionCount: review.artifact.conclusions.length,
          closedAt: review.updatedAt,
          closedReason: "revision_requested",
          supersededById: null,
          createdAt: review.createdAt,
          artifact: review.artifact,
        }}
      />
      <RunDetailsDrawer
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        review={review}
        rounds={rounds}
        run={run}
      />
    </div>
  );
}

function RevisionStep({
  description,
  icon,
  last = false,
  outcome,
  title,
  tone,
}: {
  description: string;
  icon: React.ReactNode;
  last?: boolean;
  outcome: string;
  title: string;
  tone: "muted" | "success" | "warning";
}): React.ReactElement {
  return (
    <div
      className={cn(
        "flex min-h-[65px] items-center gap-3.5 px-[18px] py-3.5",
        !last && "border-b border-line",
        tone === "warning" && "bg-amber-subtle/55",
      )}
    >
      <span
        className={cn(
          "flex size-[26px] shrink-0 items-center justify-center rounded-full text-xs font-semibold [&_svg]:size-3.5",
          tone === "success" && "bg-green/10 text-green-11",
          tone === "warning" && "bg-amber/10 text-amber-11",
          tone === "muted" && "bg-subtle text-muted-foreground",
        )}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "text-[13px] font-semibold leading-[18px]",
            tone === "muted" ? "text-muted-foreground" : "text-ink",
          )}
        >
          {title}
        </p>
        <p className="text-xs leading-4 text-muted-foreground">{description}</p>
      </div>
      {tone === "warning" ? (
        <Badge variant="warning" className="h-7 px-[9px] text-xs font-semibold">
          {outcome}
        </Badge>
      ) : (
        <span
          className={cn(
            "text-xs",
            tone === "success" ? "font-semibold text-green-11" : "text-muted-foreground",
          )}
        >
          {outcome}
        </span>
      )}
    </div>
  );
}
